import { Injectable } from '@angular/core';
import { Encounter, EncounterAPIResponse, encounterType } from '../interfaces/encounter';
import { Participant } from '../interfaces/datatypes-fhir';

@Injectable({
  providedIn: 'root',
})
@Injectable({
  providedIn: 'root',
})
export class EncounterAdapter {
  public adapterToModel(encounterFHIR: EncounterAPIResponse): Encounter {
    const organization = {
      name: encounterFHIR.serviceProvider.display,
      organization_id: encounterFHIR.serviceProvider.code,
    };

    const practitioner = {
      name: encounterFHIR.participant[0].display,
      practitioner_id: encounterFHIR.participant[0].code,
    };

    const secretary =
      encounterFHIR.participant.length > 1
        ? {
            name: encounterFHIR.participant[1].display,
            secretary_id: encounterFHIR.participant[1].code,
          }
        : undefined;

    const patient = {
      name: encounterFHIR.subject.display,
      patient_id: encounterFHIR.subject.code,
    };

    return {
      encounter_id: encounterFHIR.encounter_id,
      name: encounterFHIR.name || '',
      value: encounterFHIR.value || undefined,
      paymentMethod: encounterFHIR.paymentMethod || '',
      type: encounterFHIR.type as encounterType,
      appointment: encounterFHIR.appointment,
      status: encounterFHIR.status,
      active: encounterFHIR.active,
      specialty: encounterFHIR.specialty?.[0]?.coding[0].display,
      specialty_code: encounterFHIR.specialty?.[0].coding[0].code,
      servicetype_name: encounterFHIR.serviceType?.[0]?.coding[0].display || undefined,
      servicecategory_name: encounterFHIR.serviceCategory?.[0]?.coding[0].display || undefined,
      organization,
      secretary,
      practitioner,
      patient,
      scheduling_method: encounterFHIR.scheduling_method,
      health_plan: encounterFHIR.health_plan,
      startDate: encounterFHIR.plannedStartDate || '',
      endDate: encounterFHIR.plannedEndDate || '',
      diagnosis: encounterFHIR.diagnosis || '',
      status_payment: encounterFHIR?.status_payment,
      payment_at: encounterFHIR?.payment_at,
      byOrderInfo: encounterFHIR.byOrderInfo,
      agreement_approved: encounterFHIR?.agreement_approved,
      value_agreement: encounterFHIR?.value_agreement,
      content: encounterFHIR.content,
      patient_details: encounterFHIR.patient_details,
      cancelledReason: encounterFHIR.cancelledReason,
      attendance_code_verified: encounterFHIR.attendance_code_verified,
      modifiedBy: encounterFHIR.modifiedBy,
      created_at: encounterFHIR.created_at,
      updated_at: encounterFHIR.updated_at,
    };
  }

  adaptToAPIFormat(encounterModel: Encounter): EncounterAPIResponse {
    const participant: Participant[] = [
      {
        reference: `Practitioner/${encounterModel.practitioner.practitioner_id}`,
        code: encounterModel.practitioner.practitioner_id,
        display: encounterModel.practitioner.name,
      },
    ];

    if (encounterModel.secretary) {
      participant.push({
        reference: `Secretary/${encounterModel.secretary.secretary_id}`,
        code: encounterModel.secretary.secretary_id,
        display: encounterModel.secretary.name,
      });
    }

    return {
      encounter_id: encounterModel.encounter_id ?? undefined,
      name: encounterModel.name,
      value: encounterModel?.value || undefined,
      type: encounterModel.type,
      paymentMethod: encounterModel.paymentMethod || '',
      status: encounterModel.status,
      scheduling_method: encounterModel.scheduling_method,
      health_plan: encounterModel.health_plan,
      appointment: encounterModel.appointment,
      specialty: [{ coding: [{ code: encounterModel.specialty_code, display: encounterModel.specialty ?? '-' }] }],
      serviceType: [{ coding: [{ code: '31', display: encounterModel.servicetype_name ?? '-' }] }],
      serviceCategory: [{ coding: [{ code: '31', display: encounterModel.servicecategory_name ?? '-' }] }],
      subject: {
        reference: `Patient/${encounterModel.patient.patient_id}`,
        code: encounterModel.patient.patient_id,
        display: encounterModel.patient.name,
      },
      participant,
      serviceProvider: {
        reference: `Organization/${encounterModel.organization.organization_id}`,
        code: encounterModel.organization.organization_id,
        display: encounterModel.organization.name,
      },
      plannedStartDate: encounterModel.startDate || '',
      plannedEndDate: encounterModel.endDate || '',
      diagnosis: encounterModel.diagnosis || '',
      active: encounterModel.active,
      status_payment: encounterModel?.status_payment,
      payment_at: encounterModel?.payment_at,
      byOrderInfo: encounterModel.byOrderInfo,
      agreement_approved: encounterModel?.agreement_approved,
      value_agreement: encounterModel?.value_agreement,
      content: encounterModel.content,
      patient_details: encounterModel.patient_details,
      cancelledReason: encounterModel.cancelledReason,
      modifiedBy: encounterModel.modifiedBy,
      created_at: encounterModel.created_at,
      updated_at: encounterModel.updated_at,
    };
  }

  adaptAddDocument(documents: any[]) {
    return {
      content: documents.map(function (document) {
        return {
          document: document.base64,
          name: document.name,
        };
      }),
    };
  }
}
